import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Phone, PhoneOutgoing, Upload, Play, Download, FileText, ChevronDown, ChevronRight, X, Volume2, Loader2, Copy, DollarSign, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import CallDetailsDialog from "./CallDetailsDialog";

export default function CallsPage() {
  const [tab, setTab] = useState("calls");
  const [calls, setCalls] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ agent_id: "", to_number: "", customer_name: "" });
  const [busy, setBusy] = useState(false);

  // campaign upload
  const [campOpen, setCampOpen] = useState(false);

  // Details dialog
  const [detailsCallId, setDetailsCallId] = useState(null);
  // Expanded campaigns (show their calls)
  const [expanded, setExpanded] = useState({});
  const callsByCampaign = React.useMemo(() => {
    const m = {};
    for (const c of calls) {
      if (c.campaign_id) (m[c.campaign_id] ||= []).push(c);
    }
    return m;
  }, [calls]);

  const DEFAULT_CAMP_FORM = {
    agent_id: "",
    name: "",
    max_duration_seconds: 180,
    call_prompt: "",
    first_message: "",
    gap_seconds: 5,
    auto_launch: true,
    voice_id: "",
    from_phone_number_id: "",
    is_test: false,
    enable_end_call_function: true,
  };
  const resetCampForm = () => {
    setCampForm({ ...DEFAULT_CAMP_FORM, agent_id: agents[0]?.id || "" });
    setTestNumbers([]);
    setEstimate(null);
    setEstimateError("");
    setEstimateSample(null);
  };
  const [campForm, setCampForm] = useState(DEFAULT_CAMP_FORM);
  const fileRef = useRef(null);
  const [previewRows, setPreviewRows] = useState(null);

  // Test-Calls mode: manual phone list
  const [testNumbers, setTestNumbers] = useState([]);
  const [numberInput, setNumberInput] = useState("");

  // VAPI voices + numbers (loaded lazily on modal open)
  const [voices, setVoices] = useState([]);
  const [vapiNumbers, setVapiNumbers] = useState([]);
  const [vapiNumbersError, setVapiNumbersError] = useState("");
  const [voicePreviewing, setVoicePreviewing] = useState(null);
  const previewAudioRef = useRef(null);

  // Campaign cost estimate — queries backend for per-country Twilio pricing.
  // Kept manual (not auto-recomputed) so we don't burn Twilio Lookup credits
  // every keystroke; user clicks the Estimate button to (re)compute.
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState("");
  const [estimateSample, setEstimateSample] = useState(null); // {sampled, total} when CSV was subsampled

  const loadVoices = async () => {
    if (voices.length) return;
    try {
      const { data } = await api.get("/calls/voices");
      setVoices(data.voices || []);
    } catch { /* silent — dropdown will just show 'Use agent default' */ }
  };

  const loadVapiNumbers = async () => {
    try {
      const { data } = await api.get("/calls/vapi-numbers");
      setVapiNumbers(data.numbers || []);
      setVapiNumbersError(data.error || "");
      // Auto-select the only number if there's exactly one — matches the
      // common single-Twilio-number-connected-to-VAPI setup.
      if ((data.numbers || []).length === 1) {
        setCampForm((f) => f.from_phone_number_id ? f : { ...f, from_phone_number_id: data.numbers[0].id });
      }
    } catch (err) {
      setVapiNumbersError(err?.response?.data?.detail || "Could not load numbers from the calling server");
      setVapiNumbers([]);
    }
  };

  const previewVoice = async (voiceId) => {
    if (voicePreviewing) return;
    setVoicePreviewing(voiceId);
    try {
      const { data } = await api.post("/calls/voices/preview", { voice_id: voiceId });
      if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; }
      const audio = new Audio(`data:${data.audio_mime};base64,${data.audio_base64}`);
      previewAudioRef.current = audio;
      audio.onended = () => setVoicePreviewing(null);
      audio.onerror = () => setVoicePreviewing(null);
      await audio.play();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
      setVoicePreviewing(null);
    }
  };

  const normalizeE164 = (raw) => {
    const s = String(raw || "").split("").filter((c) => /[0-9+]/.test(c)).join("");
    if (!s.startsWith("+")) return null;
    const digits = s.slice(1);
    if (digits.length < 7 || digits.length > 18 || !/^\d+$/.test(digits)) return null;
    return s;
  };

  const addNumbers = () => {
    const raws = numberInput.split(/[,\n;\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!raws.length) return;
    const next = new Set(testNumbers);
    const bad = [];
    for (const r of raws) {
      const norm = normalizeE164(r);
      if (norm) next.add(norm);
      else bad.push(r);
    }
    setTestNumbers(Array.from(next));
    setNumberInput("");
    if (bad.length) toast.error(`Skipped ${bad.length} invalid: ${bad.slice(0, 3).join(", ")}${bad.length > 3 ? "…" : ""}`);
  };

  const removeNumber = (n) => setTestNumbers((prev) => prev.filter((x) => x !== n));

  /**
   * Duplicate an existing campaign: fetch its settings, prefill the New-
   * Campaign modal in test-numbers mode with the previous phone list, force
   * a fresh name ("<Original> (copy)") so it saves as a NEW campaign, then
   * open the modal so the user can rename / tweak / launch. All settings —
   * prompt, first message, gap, max duration, voice, from-number — are
   * carried over so the user doesn't redo the wizard.
   */
  const duplicateCampaign = async (id) => {
    try {
      const { data } = await api.get(`/calls/campaigns/${id}`);
      loadVoices();
      loadVapiNumbers();
      const numbers = Array.isArray(data.numbers) && data.numbers.length
        ? data.numbers
        : (data.contacts || []).map((c) => c.phone).filter(Boolean);
      setCampForm({
        agent_id: data.agent_id || "",
        name: `${data.name || "Campaign"} (copy)`,
        max_duration_seconds: data.max_duration_seconds || 180,
        call_prompt: data.call_prompt || "",
        first_message: data.first_message || "",
        gap_seconds: data.gap_seconds || 5,
        // Duplicates open in a review state — user consciously clicks Launch
        // in the modal so an accidental Duplicate never dials real numbers.
        auto_launch: false,
        voice_id: data.voice_id || "",
        from_phone_number_id: data.from_phone_number_id || "",
        // Force test-numbers mode so the user isn't forced to re-upload a CSV.
        is_test: true,
        enable_end_call_function: data.enable_end_call_function !== false,
      });
      setTestNumbers(numbers);
      setPreviewRows(null);
      setCampOpen(true);
      toast.success(`Loaded settings from "${data.name}" — rename and launch`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load campaign to duplicate");
    }
  };

  const previewCsv = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return setPreviewRows(null);
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    const cols = lines.slice(0, 4).map((l) => l.split(","));
    setPreviewRows({ headers: cols[0] || [], rows: cols.slice(1, 4), totalLines: Math.max(0, lines.length - 1) });
    // A new CSV invalidates any previously-shown estimate.
    setEstimate(null); setEstimateError(""); setEstimateSample(null);
  };

  /**
   * Parse the currently-selected CSV client-side to extract phone numbers
   * from the `phone` column (fallback: first column). Used solely to feed
   * the cost estimator — the real ingest still happens server-side.
   */
  const extractCsvPhones = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return { phones: [], total: 0 };
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return { phones: [], total: 0 };
    const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
    const candidates = ["phone", "number", "mobile", "tel", "cell", "telephone", "phone_number"];
    let idx = headers.findIndex((h) => candidates.includes(h));
    if (idx === -1) idx = 0;
    const phones = lines.slice(1)
      .map((r) => (r.split(",")[idx] || "").trim().replace(/^"|"$/g, ""))
      .filter(Boolean);
    return { phones, total: phones.length };
  };

  /**
   * Ask the backend for a per-country cost breakdown. For very large CSVs
   * we sample the first 200 numbers and linearly extrapolate — every
   * uncached number costs a Twilio Lookup (~$0.005), so pricing 10k rows
   * upfront would burn real money for no incremental accuracy gain.
   */
  const estimateCost = async () => {
    setEstimating(true);
    setEstimateError("");
    try {
      let phones = [];
      let total = 0;
      let sampled = false;
      if (campForm.is_test) {
        phones = testNumbers;
        total = testNumbers.length;
      } else {
        const { phones: p, total: t } = await extractCsvPhones();
        total = t;
        if (p.length > 200) { phones = p.slice(0, 200); sampled = true; }
        else phones = p;
      }
      if (!phones.length) { setEstimateError("Add numbers or upload a CSV first"); setEstimate(null); return; }
      const { data } = await api.post("/phone-numbers/pricing/estimate-campaign", {
        numbers: phones,
        max_duration_seconds: campForm.max_duration_seconds || 180,
      });
      const scale = sampled && phones.length > 0 ? total / phones.length : 1;
      setEstimate({
        ...data,
        scaled: sampled,
        total_display: (data.total || 0) * scale,
        total_contacts: total,
        per_country: (data.per_country || []).map((c) => ({
          ...c,
          count_display: sampled ? Math.round(c.count * scale) : c.count,
          estimate_display: c.estimate_max != null ? c.estimate_max * scale : null,
        })),
      });
      setEstimateSample(sampled ? { sampled: phones.length, total } : null);
    } catch (err) {
      setEstimateError(err?.response?.data?.detail || "Estimate failed");
      setEstimate(null);
    } finally { setEstimating(false); }
  };

  const load = async () => {
    const [c, cp, ag] = await Promise.all([
      api.get("/calls/list"), api.get("/calls/campaigns"), api.get("/agents"),
    ]);
    setCalls(c.data); setCampaigns(cp.data); setAgents(ag.data);
    if (ag.data[0]) {
      setForm((f) => f.agent_id ? f : { ...f, agent_id: ag.data[0].id });
      setCampForm((f) => f.agent_id ? f : { ...f, agent_id: ag.data[0].id });
    }
  };
  useEffect(() => { load(); }, []);

  // Invalidate any shown cost estimate when its inputs change — otherwise
  // users could Launch a campaign with a stale "was $2.10" number visible
  // after adding 500 more contacts or bumping max duration.
  useEffect(() => {
    if (estimate) { setEstimate(null); setEstimateSample(null); }
  }, [testNumbers, campForm.max_duration_seconds, campForm.is_test]);

  // Auto-refresh campaign progress + associated calls every 5s while anything
  // is still running/pending. Poll both endpoints in parallel so newly-dialled
  // calls surface without a manual refresh.
  useEffect(() => {
    const has_running = campaigns.some((c) => c.status === "running" || c.status === "pending");
    if (!has_running) return;
    const t = setInterval(() => {
      Promise.all([api.get("/calls/campaigns"), api.get("/calls/list")])
        .then(([cp, c]) => { setCampaigns(cp.data); setCalls(c.data); })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [campaigns]);

  const startCall = async () => {
    setBusy(true);
    try {
      await api.post("/calls/outbound", form);
      toast.success("Call queued");
      setOpen(false); setForm({ ...form, to_number: "", customer_name: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start call");
    } finally { setBusy(false); }
  };

  const uploadCampaign = async () => {
    if (!campForm.name || !campForm.agent_id) return toast.error("Pick an agent and give the campaign a name");
    // TEST-CALLS MODE — send JSON to /calls/campaigns
    if (campForm.is_test) {
      if (testNumbers.length === 0) return toast.error("Add at least one phone number to test");
      if (vapiNumbers.length > 0 && !campForm.from_phone_number_id) {
        return toast.error("Pick a 'Call from' number");
      }
      setBusy(true);
      try {
        const { data } = await api.post("/calls/campaigns", {
          name: campForm.name,
          agent_id: campForm.agent_id,
          numbers: testNumbers,
          max_duration_seconds: campForm.max_duration_seconds || 180,
          call_prompt: campForm.call_prompt || null,
          first_message: campForm.first_message || null,
          gap_seconds: campForm.gap_seconds || 5,
          voice_id: campForm.voice_id || null,
          from_phone_number_id: campForm.from_phone_number_id || null,
          auto_launch: campForm.auto_launch,
          is_test: true,
          enable_end_call_function: campForm.enable_end_call_function,
        });
        toast.success(data.auto_launched ? `Test campaign launched · ${data.total} calls` : `Test campaign created · ${data.total} calls (click Launch)`);
        setCampOpen(false);
        setCampForm({ ...campForm, name: "" });
        setTestNumbers([]);
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to create test campaign");
      } finally { setBusy(false); }
      return;
    }
    // CSV MODE
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Pick a CSV file (or switch to Test Calls mode)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("agent_id", campForm.agent_id);
      fd.append("name", campForm.name);
      fd.append("max_duration_seconds", String(campForm.max_duration_seconds || 180));
      fd.append("call_prompt", campForm.call_prompt || "");
      fd.append("first_message", campForm.first_message || "");
      fd.append("gap_seconds", String(campForm.gap_seconds || 5));
      fd.append("auto_launch", campForm.auto_launch ? "true" : "false");
      fd.append("voice_id", campForm.voice_id || "");
      fd.append("from_phone_number_id", campForm.from_phone_number_id || "");
      fd.append("enable_end_call_function", campForm.enable_end_call_function ? "true" : "false");
      const { data } = await api.post("/calls/campaigns/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.auto_launched ? `Campaign launched · ${data.total} contacts` : `Campaign created · ${data.total} contacts (click Launch)`);
      setCampOpen(false);
      setCampForm({ ...campForm, name: "" });
      setPreviewRows(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const launch = async (id) => {
    try {
      await api.post(`/calls/campaigns/${id}/launch`);
      toast.success("Campaign launching");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="calls-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl">Calls & Campaigns</h1>
          <p className="text-sm text-[#4B5563]">Outbound voice campaigns · dialed via Twilio, orchestrated by our calling server.</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-outline text-sm flex items-center gap-2" data-testid="new-outbound-call-button"><PhoneOutgoing className="w-4 h-4" /> One-off call</button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Start an outbound call</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium block mb-1.5">Agent</label>
                  <select data-testid="outbound-agent-select" value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })} className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm bg-white">
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1.5">Phone (E.164)</label>
                  <input data-testid="outbound-number-input" value={form.to_number} onChange={(e) => setForm({ ...form, to_number: e.target.value })} placeholder="+447700900123" className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1.5">Customer name (optional)</label>
                  <input data-testid="outbound-name-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm" />
                </div>
                <button data-testid="outbound-start-button" disabled={busy || !form.to_number} onClick={startCall} className="btn-primary w-full">{busy ? "Calling…" : "Start call"}</button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={campOpen} onOpenChange={(v) => { setCampOpen(v); if (v) { loadVoices(); loadVapiNumbers(); } if (!v) { setPreviewRows(null); setEstimate(null); setEstimateError(""); setEstimateSample(null); } }}>
            <DialogTrigger asChild>
              <button
                onClick={resetCampForm}
                className="btn-primary text-sm flex items-center gap-2"
                data-testid="new-campaign-button"
              ><Upload className="w-4 h-4" /> New campaign</button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
              <DialogHeader><DialogTitle>New outbound campaign</DialogTitle></DialogHeader>
              <div className="space-y-4">
                {/* Test Calls toggle — top-of-modal */}
                <label className="flex items-start gap-2 text-xs cursor-pointer p-3 border border-[#E2E8F0] rounded-sm bg-[#FFF7ED]/40" data-testid="campaign-test-mode-label">
                  <input
                    type="checkbox"
                    data-testid="campaign-test-mode"
                    checked={campForm.is_test}
                    onChange={(e) => setCampForm({ ...campForm, is_test: e.target.checked })}
                    className="mt-0.5 accent-[#F97316]"
                  />
                  <span>
                    <span className="font-semibold text-sm">Test Calls</span>
                    <span className="block text-[11px] text-[#4B5563] mt-0.5">Skip the CSV. Add a few numbers manually to sanity-check your prompt before you launch to a real list.</span>
                  </span>
                </label>

                {/* Basics */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-mono uppercase text-[#4B5563]">Agent</label>
                    <select data-testid="campaign-agent-select" value={campForm.agent_id} onChange={(e) => setCampForm({ ...campForm, agent_id: e.target.value })} className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm bg-white text-sm mt-1">
                      <option value="" disabled>— Select an agent —</option>
                      {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-mono uppercase text-[#4B5563]">Campaign name</label>
                    <input data-testid="campaign-name-input" value={campForm.name} onChange={(e) => setCampForm({ ...campForm, name: e.target.value })} placeholder="Sep 2026 prospects" className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm mt-1" />
                  </div>
                </div>

                {/* Voice — pulled from /calls/voices (VAPI OpenAI provider) */}
                <div className="border border-[#E2E8F0] p-3 rounded-sm space-y-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-2">Call from (registered number)</div>
                    {vapiNumbers.length > 0 ? (
                      <select
                        data-testid="campaign-from-number-select"
                        value={campForm.from_phone_number_id}
                        onChange={(e) => setCampForm({ ...campForm, from_phone_number_id: e.target.value })}
                        className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm bg-white text-sm"
                      >
                        <option value="">Choose a number…</option>
                        {vapiNumbers.map((n) => (
                          <option key={n.id} value={n.id}>
                            {n.number} — {n.name} · {n.provider}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-[11px] px-3 py-2 border border-dashed border-[#F97316]/40 bg-[#FFF7ED] rounded-sm" data-testid="campaign-no-vapi-numbers">
                        {vapiNumbersError
                          ? <>Could not load calling-server numbers: <span className="font-mono">{vapiNumbersError}</span></>
                          : <>No calling-server numbers connected yet. Connect a Twilio (or other) number in <a href="https://dashboard.vapi.ai/phone-numbers" target="_blank" rel="noopener noreferrer" className="underline text-[#EA580C]">your phone-number registrations</a> first.</>}
                      </div>
                    )}
                    <div className="text-[11px] text-[#4B5563] mt-1">This is the number the calling server will dial <strong>from</strong>. Required — the previous auto-fallback to env Twilio creds only works if that number is provisioned via VAPI too.</div>
                  </div>

                  <div>
                    <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-2">Calling agent voice</div>
                    <div className="flex items-center gap-2">
                      <select
                        data-testid="campaign-voice-select"
                        value={campForm.voice_id}
                        onChange={(e) => setCampForm({ ...campForm, voice_id: e.target.value })}
                        className="flex-1 border border-[#E2E8F0] px-3 py-2 rounded-sm bg-white text-sm"
                      >
                        <option value="">Use the agent&apos;s default voice</option>
                        {voices.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name} — {v.gender}, {v.accent} · {v.style}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => campForm.voice_id && previewVoice(campForm.voice_id)}
                        disabled={!campForm.voice_id || !!voicePreviewing}
                        className="btn-outline text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-50"
                        data-testid="campaign-voice-preview"
                        title="Preview selected voice"
                      >
                        {voicePreviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
                        Preview
                      </button>
                    </div>
                    <div className="text-[11px] text-[#4B5563] mt-1">Override the agent&apos;s voice for this campaign only.</div>
                  </div>
                </div>

                {/* Call settings */}
                <div className="border border-[#E2E8F0] p-3 rounded-sm">
                  <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-2">Call settings</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs">Max duration per call <span className="font-mono text-[10px] text-[#4B5563]">(seconds, 30–1800)</span></label>
                      <input
                        data-testid="campaign-max-duration"
                        type="number" min={30} max={1800} step={10}
                        value={campForm.max_duration_seconds}
                        onChange={(e) => setCampForm({ ...campForm, max_duration_seconds: parseInt(e.target.value || "180", 10) })}
                        className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm mt-1 font-mono"
                      />
                      <div className="text-[10px] text-[#4B5563] mt-0.5">The call hangs up after this. 180 = 3 minutes.</div>
                    </div>
                    <div>
                      <label className="text-xs">Gap between calls <span className="font-mono text-[10px] text-[#4B5563]">(seconds)</span></label>
                      <input
                        data-testid="campaign-gap-seconds"
                        type="number" min={1} max={60} step={1}
                        value={campForm.gap_seconds}
                        onChange={(e) => setCampForm({ ...campForm, gap_seconds: parseInt(e.target.value || "5", 10) })}
                        className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm mt-1 font-mono"
                      />
                      <div className="text-[10px] text-[#4B5563] mt-0.5">Avoid Twilio rate limits.</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="text-xs">Opening line (optional — overrides agent welcome)</label>
                    <input
                      data-testid="campaign-first-message"
                      value={campForm.first_message}
                      onChange={(e) => setCampForm({ ...campForm, first_message: e.target.value })}
                      placeholder="Hi {{name}}, this is Sarah from IEO Global, calling about your interest in {{course}}…"
                      className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm mt-1"
                    />
                    <div className="text-[10px] text-[#4B5563] mt-0.5">Use <code className="font-mono">{`{{column_name}}`}</code> to personalise per row.</div>
                  </div>
                  <div className="mt-3">
                    <label className="text-xs">Call prompt (optional — overrides agent system prompt)</label>
                    <textarea
                      data-testid="campaign-call-prompt"
                      rows={3}
                      value={campForm.call_prompt}
                      onChange={(e) => setCampForm({ ...campForm, call_prompt: e.target.value })}
                      placeholder="You are a friendly admissions advisor calling prospective students. Confirm their interest, answer course questions, and offer to book a counselling session."
                      className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-xs font-mono mt-1"
                    />
                    <div className="text-[10px] text-[#4B5563] mt-0.5">Leave blank to use the agent&apos;s own prompt + knowledge base.</div>
                  </div>
                  <label className="mt-3 flex items-start gap-2 text-xs cursor-pointer" data-testid="campaign-endcall-label">
                    <input
                      type="checkbox"
                      data-testid="campaign-enable-endcall"
                      checked={campForm.enable_end_call_function}
                      onChange={(e) => setCampForm({ ...campForm, enable_end_call_function: e.target.checked })}
                      className="mt-0.5 accent-[#F97316]"
                    />
                    <span>
                      <span className="font-semibold">Enable End Call function</span>
                      <span className="block text-[10px] text-[#4B5563] mt-0.5">Lets the assistant politely hang up once the caller&apos;s intent is resolved, instead of running out the max-duration timer. Recommended.</span>
                    </span>
                  </label>
                </div>

                {/* Contacts — either CSV upload or manual numbers depending on Test Calls mode */}
                {!campForm.is_test ? (
                  <div className="border border-[#E2E8F0] p-3 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] font-mono uppercase text-[#4B5563]">Contacts CSV</div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/calls/campaigns/sample.csv`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-[#F97316] font-medium hover:underline flex items-center gap-1"
                        data-testid="campaign-sample-csv-link"
                      >
                        <Download className="w-3 h-3" /> Download sample CSV
                      </a>
                    </div>
                    <input
                      data-testid="campaign-file-input"
                      type="file"
                      accept=".csv,text/csv"
                      ref={fileRef}
                      onChange={previewCsv}
                      className="w-full text-sm"
                    />
                    <div className="text-[11px] text-[#4B5563] mt-2 leading-relaxed">
                      Required column: <code className="font-mono">phone</code> (E.164 like <code className="font-mono">+447700900123</code>).<br />
                      Optional columns (e.g. <code className="font-mono">name, course, intake, language, notes</code>) are passed to the agent as variables — the agent will read them during the call and personalise the conversation.
                    </div>

                    {previewRows && (
                      <div className="mt-3 border border-[#E2E8F0] overflow-auto" data-testid="campaign-csv-preview">
                        <div className="text-[10px] font-mono uppercase text-[#4B5563] px-2 py-1 bg-[#F3F4F6]">
                          preview · {previewRows.headers.length} columns · {previewRows.totalLines} rows
                        </div>
                        <table className="w-full text-[11px] font-mono">
                          <thead className="bg-[#F9FAFB]">
                            <tr>{previewRows.headers.map((h, i) => <th key={i} className="text-left px-2 py-1 border-b border-[#E2E8F0]">{h}</th>)}</tr>
                          </thead>
                          <tbody>
                            {previewRows.rows.map((r, i) => (
                              <tr key={i} className="border-t border-[#E2E8F0]">
                                {r.map((c, j) => <td key={j} className="px-2 py-1 truncate max-w-[140px]">{c}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border border-[#F97316]/40 bg-[#FFF7ED]/50 p-3 rounded-sm" data-testid="campaign-test-numbers-block">
                    <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-2">Test numbers</div>
                    <div className="flex items-start gap-2">
                      <textarea
                        data-testid="campaign-test-numbers-input"
                        value={numberInput}
                        onChange={(e) => setNumberInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addNumbers(); } }}
                        placeholder={"+447700900123, +14155559821\nOr paste one number per line — E.164 only"}
                        rows={2}
                        className="flex-1 border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm font-mono resize-y"
                      />
                      <button
                        type="button"
                        onClick={addNumbers}
                        className="btn-primary text-xs px-4 py-2"
                        data-testid="campaign-test-numbers-add"
                      >
                        Add
                      </button>
                    </div>
                    <div className="text-[10px] text-[#4B5563] mt-1">Paste multiple separated by comma, newline, semicolon, or space. Numbers must start with <code className="font-mono">+</code> (E.164).</div>

                    {testNumbers.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="campaign-test-numbers-chips">
                        {testNumbers.map((n) => (
                          <span
                            key={n}
                            className="inline-flex items-center gap-1.5 bg-white border border-[#F97316]/40 text-[#EA580C] font-mono text-xs px-2 py-1 rounded-sm"
                          >
                            {n}
                            <button
                              type="button"
                              onClick={() => removeNumber(n)}
                              className="hover:text-[#0F1115]"
                              aria-label={`Remove ${n}`}
                              data-testid={`campaign-test-number-remove-${n}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                        <span className="ml-auto text-[10px] font-mono text-[#4B5563] self-center">{testNumbers.length} number{testNumbers.length === 1 ? "" : "s"} · will dial with the exact prompt above</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Cost estimator — live Twilio per-country rates */}
                <div className="border border-[#E2E8F0] p-3 rounded-sm" data-testid="campaign-cost-estimator">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-mono uppercase text-[#4B5563]">Estimated Twilio telecom cost</div>
                      <div className="text-[11px] text-[#4B5563] mt-0.5">
                        Worst-case (Twilio&apos;s max per-minute rate per country) × max call length. VAPI + AI usage bill separately.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={estimateCost}
                      disabled={estimating}
                      className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1.5 shrink-0"
                      data-testid="campaign-estimate-cost-button"
                    >
                      {estimating
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Pricing…</>
                        : estimate
                          ? <><RefreshCw className="w-3.5 h-3.5" /> Recalculate</>
                          : <><DollarSign className="w-3.5 h-3.5" /> Estimate cost</>}
                    </button>
                  </div>

                  {estimateError && (
                    <div className="text-[11px] text-[#EF4444] mt-2" data-testid="campaign-estimate-error">{estimateError}</div>
                  )}

                  {estimate && (
                    <div className="mt-3 space-y-2" data-testid="campaign-estimate-result">
                      <div className="flex items-baseline gap-3">
                        <div className="text-3xl font-display font-extrabold text-[#EA580C]" data-testid="campaign-estimate-total">
                          ~${(estimate.scaled ? estimate.total_display : estimate.total).toFixed(2)}
                          <span className="text-sm font-mono font-normal text-[#4B5563] ml-1">{estimate.currency || "USD"}</span>
                        </div>
                        <div className="text-[11px] text-[#4B5563] font-mono">
                          {estimate.total_contacts || estimate.contacts_priced + estimate.contacts_unknown} contacts · {estimate.minutes_per_call?.toFixed(1)} min max/call
                        </div>
                      </div>
                      {estimate.scaled && estimateSample && (
                        <div className="text-[10px] text-[#F97316] font-mono" data-testid="campaign-estimate-scaled-note">
                          extrapolated from a {estimateSample.sampled}-number sample of {estimateSample.total}
                        </div>
                      )}
                      {estimate.per_country && estimate.per_country.length > 0 && (
                        <div className="border border-[#E2E8F0] mt-2 overflow-auto max-h-48">
                          <table className="w-full text-xs">
                            <thead className="bg-[#F9FAFB] text-[10px] uppercase font-mono text-[#4B5563] sticky top-0">
                              <tr>
                                <th className="text-left px-2 py-1">Country</th>
                                <th className="text-right px-2 py-1">Contacts</th>
                                <th className="text-right px-2 py-1">Rate/min</th>
                                <th className="text-right px-2 py-1">Subtotal</th>
                              </tr>
                            </thead>
                            <tbody data-testid="campaign-estimate-breakdown">
                              {estimate.per_country.map((c) => (
                                <tr key={c.iso} className="border-t border-[#E2E8F0]" data-testid={`campaign-estimate-row-${c.iso}`}>
                                  <td className="px-2 py-1 font-mono">
                                    <span className={c.iso === "??" ? "text-[#4B5563] italic" : ""}>{c.iso}</span>
                                    <span className="text-[#4B5563] ml-1">{c.name}</span>
                                  </td>
                                  <td className="text-right px-2 py-1 font-mono">{c.count_display ?? c.count}</td>
                                  <td className="text-right px-2 py-1 font-mono">
                                    {c.rate_per_min_max != null ? `$${c.rate_per_min_max.toFixed(4)}` : "—"}
                                  </td>
                                  <td className="text-right px-2 py-1 font-mono">
                                    {c.estimate_display != null ? `$${c.estimate_display.toFixed(2)}` : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {estimate.contacts_unknown > 0 && (
                        <div className="text-[10px] text-[#F97316]" data-testid="campaign-estimate-unknown-note">
                          ⚠ {estimate.contacts_unknown} number{estimate.contacts_unknown === 1 ? "" : "s"} couldn&apos;t be resolved to a country — not priced.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <label className="flex items-start gap-2 text-xs cursor-pointer p-2 border border-[#E2E8F0] rounded-sm" data-testid="campaign-autolaunch-label">
                  <input
                    type="checkbox"
                    data-testid="campaign-autolaunch"
                    checked={campForm.auto_launch}
                    onChange={(e) => setCampForm({ ...campForm, auto_launch: e.target.checked })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium">Launch immediately after upload</span>
                    <span className="block text-[10px] text-[#4B5563] mt-0.5">If unchecked, the campaign stays in &quot;pending&quot; until you click Launch.</span>
                  </span>
                </label>

                <button data-testid="campaign-upload-button" disabled={busy} onClick={uploadCampaign} className="btn-primary w-full">
                  {busy
                    ? (campForm.is_test ? "Launching test…" : "Uploading…")
                    : campForm.is_test
                      ? (campForm.auto_launch ? `Launch test on ${testNumbers.length || 0} number${testNumbers.length === 1 ? "" : "s"}` : "Create test campaign")
                      : (campForm.auto_launch ? "Create & launch campaign" : "Create campaign")}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[#E2E8F0]">
        {[["calls","Calls"],["campaigns","Campaigns"]].map(([k,l]) => (
          <button key={k} data-testid={`calls-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === k ? "border-[#0044FF] text-[#030712]" : "border-transparent text-[#4B5563]"}`}>{l}</button>
        ))}
      </div>

      {tab === "calls" && (
        <div className="hs-card overflow-hidden">
          {calls.length === 0 ? <div className="p-8 text-center text-sm text-[#4B5563]">No calls yet.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-[#F3F4F6] text-[11px] uppercase font-mono text-[#4B5563]">
                <tr><th className="text-left px-4 py-3">Direction</th><th className="text-left px-4 py-3">Number</th><th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Created</th><th className="px-4 py-3" /></tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <tr key={c.id} className="border-t border-[#E2E8F0] hover:bg-[#F9FAFB]" data-testid={`call-row-${c.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{c.direction}</td>
                    <td className="px-4 py-3 font-mono">{c.to_number || c.from_number}</td>
                    <td className="px-4 py-3">{c.customer_name || "—"}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 ${c.status === "ended" || c.status === "completed" ? "bg-[#10B981]/10 text-[#10B981]" : c.status === "failed" ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#F3F4F6]"}`}>{c.status}</span></td>
                    <td className="px-4 py-3 text-xs text-[#4B5563]">{c.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setDetailsCallId(c.id)} className="text-xs text-[#0044FF] font-medium flex items-center gap-1 ml-auto" data-testid={`call-details-${c.id}`}>
                        <FileText className="w-3 h-3" /> Details →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "campaigns" && (
        <div className="space-y-3">          {campaigns.length === 0 ? (
            <div className="hs-card p-12 text-center">
              <Phone className="w-10 h-10 mx-auto text-[#4B5563]" />
              <div className="font-display font-bold mt-4">No campaigns yet</div>
              <div className="text-sm text-[#4B5563]">Upload a CSV to start.</div>
            </div>
          ) : campaigns.map((c) => {
            const campCalls = callsByCampaign[c.id] || [];
            const isOpen = !!expanded[c.id];
            return (
              <div key={c.id} className="hs-card" data-testid={`campaign-row-${c.id}`}>
                <div className="p-4 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => setExpanded({ ...expanded, [c.id]: !isOpen })}
                      className="flex items-center gap-2 font-display font-bold text-left"
                      data-testid={`campaign-toggle-${c.id}`}
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      {c.name}
                    </button>
                    <div className="text-xs text-[#4B5563] font-mono ml-6">
                      {c.total} contacts · <span className={c.status === "failed" ? "text-[#EF4444] font-bold" : c.status === "completed" ? "text-[#10B981] font-bold" : ""}>{c.status}</span>
                      {c.created_at && <span className="ml-2 text-[#94A3B8]" data-testid={`campaign-created-${c.id}`}>· created {String(c.created_at).slice(0, 16).replace("T", " ")}</span>}
                    </div>
                    <div className="w-64 h-1 bg-[#E2E8F0] mt-2 overflow-hidden ml-6">
                      <div className="h-full bg-[#0044FF]" style={{ width: `${(c.completed / Math.max(1, c.total)) * 100}%` }} />
                    </div>
                    <div className="text-[11px] font-mono text-[#4B5563] mt-1 ml-6">{c.completed} / {c.total}</div>
                    {c.error && <div className="text-[11px] text-[#EF4444] mt-1.5 max-w-md ml-6" data-testid={`campaign-error-${c.id}`}>⚠ {c.error}</div>}
                  </div>
                  {c.status === "pending" && (
                    <button onClick={() => launch(c.id)} className="btn-primary text-sm flex items-center gap-1" data-testid={`campaign-launch-${c.id}`}><Play className="w-4 h-4" /> Launch</button>
                  )}
                  {c.status === "failed" && (
                    <button onClick={() => launch(c.id)} className="btn-outline text-sm flex items-center gap-1" data-testid={`campaign-relaunch-${c.id}`}><Play className="w-4 h-4" /> Retry</button>
                  )}
                  <button
                    onClick={() => duplicateCampaign(c.id)}
                    className="btn-outline text-sm flex items-center gap-1 ml-2"
                    data-testid={`campaign-duplicate-${c.id}`}
                    title="Duplicate — opens the New-Campaign form pre-filled with these settings"
                  >
                    <Copy className="w-4 h-4" /> Duplicate
                  </button>
                </div>
                {isOpen && (
                  <div className="border-t border-[#E2E8F0] bg-[#F9FAFB]" data-testid={`campaign-calls-${c.id}`}>
                    {campCalls.length === 0 ? (
                      <div className="p-4 text-xs text-[#4B5563]">No calls dispatched yet.</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase font-mono text-[#4B5563]">
                          <tr><th className="text-left px-4 py-2">Number</th><th className="text-left px-4 py-2">Customer</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Created</th><th className="px-4 py-2" /></tr>
                        </thead>
                        <tbody>
                          {campCalls.map((cc) => (
                            <React.Fragment key={cc.id}>
                              <tr className="border-t border-[#E2E8F0]">
                                <td className="px-4 py-2 font-mono">{cc.to_number}</td>
                                <td className="px-4 py-2">{cc.customer_name || "—"}</td>
                                <td className="px-4 py-2"><span className={`text-[10px] font-mono px-2 py-0.5 ${cc.status === "ended" || cc.status === "completed" ? "bg-[#10B981]/10 text-[#10B981]" : cc.status === "failed" ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#F3F4F6]"}`}>{cc.status}</span></td>
                                <td className="px-4 py-2 text-[#4B5563]">{cc.created_at?.slice(0, 16).replace("T", " ")}</td>
                                <td className="px-4 py-2 text-right">
                                  <button onClick={() => setDetailsCallId(cc.id)} className="text-xs text-[#0044FF] font-medium flex items-center gap-1 ml-auto" data-testid={`call-details-${cc.id}`}>
                                    <FileText className="w-3 h-3" /> Recording / transcript →
                                  </button>
                                </td>
                              </tr>
                              {cc.friendly_error && (
                                <tr className="border-t-0 bg-[#FEF3C7]" data-testid={`call-friendly-error-${cc.id}`}>
                                  <td colSpan={5} className="px-4 py-2 text-[11px] text-[#92400E] font-medium">
                                    {cc.friendly_error}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CallDetailsDialog
        callId={detailsCallId}
        open={!!detailsCallId}
        onOpenChange={(v) => { if (!v) setDetailsCallId(null); }}
      />
    </div>
  );
}
